import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import EmailVerifyBanner from '@/components/EmailVerifyBanner';
import CoverCropper from '@/components/CoverCropper';
import { ShieldCheck, Camera, Images, X, Plus, Trash, CheckCircle, CurrencyDollar, CalendarBlank, Bank, FileArrowUp, FileText } from '@phosphor-icons/react';
import { ALL_CATEGORIES } from '@/lib/serviceCategories';
import { toast } from '@/components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Categories come from the shared source of truth so signup, Browse filters and the
// landing-page links can never drift apart (a mismatch silently returns 0 providers).
const CATEGORIES = [...ALL_CATEGORIES, 'Other'];
const UNITS = [
  { value: 'fixed', label: 'flat / per event' },
  { value: 'hourly', label: 'per hour' },
  { value: 'per_person', label: 'per person' },
  { value: 'range', label: 'price range (min–max)' },
  { value: 'quote', label: 'custom quote (no price)' },
];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
// Full 24-hour list (00:00–23:00) so late-night vendors (DJs, bartenders,
// security) can set hours that run into the early morning, e.g. 20:00–03:00.
const TIMES = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);

const PERKS = [
  { icon: '💸', title: 'Only 6% cut', desc: 'No subscriptions. We only earn when you earn.' },
  { icon: '📲', title: 'Clients find you', desc: 'No marketing needed. Bookings come to you.' },
  { icon: '💳', title: 'Fast payouts', desc: 'Paid right after service is confirmed complete.' },
  { icon: '🤖', title: 'AI sends you clients', desc: 'The planner recommends you based on fit.' },
];

const STEPS = ['Apply', 'Review (24h)', 'Start earning'];

// Per-provider steps for finding the secret .ics link — the #1 place vendors
// get stuck connecting a calendar. Wording verified against each provider's
// current export/publish UI.
const CAL_HELP = [
  { name: 'Google Calendar', emoji: '📅', steps: [
    'On a computer, open Google Calendar → Settings (gear icon) → Settings.',
    'Under "Settings for my calendars", click the calendar you want.',
    'Scroll to "Integrate calendar" and copy the "Secret address in iCal format" (ends in .ics).',
  ] },
  { name: 'Apple / iCloud Calendar', emoji: '🍎', steps: [
    'On iCloud.com or the Calendar app, make the calendar a Public Calendar (share icon → "Public Calendar").',
    'Copy the link it gives you (it starts with webcal:// — that’s fine, paste it as-is).',
  ] },
  { name: 'Outlook / Office 365', emoji: '📧', steps: [
    'In Outlook on the web: Settings → Calendar → Shared calendars → Publish a calendar.',
    'Pick the calendar and "Can view all details", then Publish.',
    'Copy the ICS link (not the HTML one).',
  ] },
  { name: 'Other apps (Fastmail, Proton, Yahoo…)', emoji: '🗓️', steps: [
    'Look in your calendar’s settings for "Publish", "Public link", or "Export/Subscribe".',
    'Copy the link ending in .ics (or starting with webcal://).',
  ] },
];

// Static style strings + the Section wrapper live at MODULE scope (not inside the
// component). Defining a component inside another re-creates it on every keystroke,
// which unmounts/remounts the inputs and drops focus — so you'd have to click the
// box again for each letter. Keeping these stable lets React reuse the same inputs.
const inputCls = "w-full h-11 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus:ring-2 focus:ring-primary/40";
const labelCls = "block text-sm font-medium text-foreground mb-1.5";
const Section = ({ n, title, subtitle, children }) => (
  <div className="border-t border-border/60 pt-6 first:border-t-0 first:pt-0">
    <div className="flex items-baseline gap-2 mb-4">
      <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center shrink-0">{n}</span>
      <div>
        <h3 className="text-base font-display font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

export default function ProviderOnboarding() {
  const { user, token, checkAuth, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [payoutBusy, setPayoutBusy] = useState(false);
  // Edit mode: the provider already has a profile, so we pre-fill and PUT (update)
  // rather than POST (which would create a duplicate profile).
  const [editMode, setEditMode] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const loadedRef = useRef(false);

  const [form, setForm] = useState({
    business_name: '', first_name: '', last_name: '', email: '', phone: '',
    category: '', city: '', service_radius: '25', experience: '',
    description: '', instagram: '', deposit_pct: '0', cancellation_policy: 'flexible',
    cancel_fee_window_days: '7', cancel_fee_type: 'percent', cancel_fee_amount: '50',
    special_offer: '', video_url: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Services / packages the provider offers (at least one required). service_id is set
  // for rows loaded from an existing profile so edits update in place instead of duplicating.
  const [services, setServices] = useState([{ service_id: null, title: '', description: '', price: '', price_min: '', price_max: '', unit: 'fixed' }]);
  const setService = (i, k, v) => setServices(s => s.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const addService = () => setServices(s => (s.length >= 6 ? s : [...s, { service_id: null, title: '', description: '', price: '', price_min: '', price_max: '', unit: 'fixed' }]));
  const removeService = (i) => setServices(s => (s.length === 1 ? s : s.filter((_, j) => j !== i)));

  // Weekly availability
  const [workDays, setWorkDays] = useState([true, true, true, true, true, true, true]);
  const [workStart, setWorkStart] = useState('09:00');
  const [workEnd, setWorkEnd] = useState('22:00');
  const toggleDay = (i) => setWorkDays(d => d.map((on, j) => (j === i ? !on : on)));

  const [accepted, setAccepted] = useState(false);

  // Photo uploads
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [coverUrl, setCoverUrl] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverCropFile, setCoverCropFile] = useState(null);   // file awaiting crop
  const [photos, setPhotos] = useState([]);
  const [photosBusy, setPhotosBusy] = useState(false);
  const [videoFile, setVideoFile] = useState(null);   // uploaded portfolio video (path)
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoErr, setVideoErr] = useState('');
  const [documents, setDocuments] = useState([]);      // [{name, path}] menu / press kit / pricing PDFs
  const [docBusy, setDocBusy] = useState(false);
  const [docErr, setDocErr] = useState('');
  // Calendar sync
  const [feedUrl, setFeedUrl] = useState('');          // our subscribe link for them
  const [feedCopied, setFeedCopied] = useState(false);
  const [extCalUrl, setExtCalUrl] = useState('');      // their own calendar link
  const [calBusy, setCalBusy] = useState(false);
  const [calMsg, setCalMsg] = useState('');
  const backendUrl = (u) => (u && u.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  // On mount (once auth resolves), load the provider's existing profile + services and
  // pre-fill the form. A 404 means they have no profile yet → first-time signup (edit
  // mode stays off). Sessions may be cookie-based (token null), so rely on withCredentials
  // — already the axios default — and add the bearer only when we have one.
  useEffect(() => {
    if (loadedRef.current || authLoading) return;
    loadedRef.current = true;
    const auth = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
    (async () => {
      try {
        const prof = await axios.get(`${API}/providers/profile/me`, auth)
          .then(r => r.data)
          .catch(err => { if (err?.response?.status === 404) return null; throw err; });
        if (prof) {
          setEditMode(true);
          const rules = prof.availability_rules || {};
          setForm(f => ({
            ...f,
            business_name: prof.business_name || '',
            category: prof.category || '',
            city: prof.city || '',
            service_radius: prof.service_radius_km != null ? String(prof.service_radius_km) : '25',
            experience: prof.years_experience != null ? String(prof.years_experience) : '',
            description: prof.description || '',
            instagram: prof.instagram || '',
            deposit_pct: prof.deposit_pct != null ? String(prof.deposit_pct) : '0',
            cancellation_policy: prof.cancellation_policy || 'flexible',
            cancel_fee_window_days: prof.cancel_fee_window_days != null ? String(prof.cancel_fee_window_days) : '7',
            cancel_fee_type: prof.cancel_fee_type || 'percent',
            cancel_fee_amount: prof.cancel_fee_amount != null ? String(prof.cancel_fee_amount) : '50',
            special_offer: prof.special_offer || '',
            // A portfolio video is either an uploaded file (/api/files/…) or a link.
            video_url: (prof.portfolio_videos || []).find(v => !String(v).startsWith('/api/files/')) || '',
          }));
          const uploadedVid = (prof.portfolio_videos || []).find(v => String(v).startsWith('/api/files/'));
          if (uploadedVid) setVideoFile(uploadedVid);
          if (Array.isArray(prof.documents)) setDocuments(prof.documents);
          setPhotos(prof.portfolio_images || []);
          if (prof.cover_image) setCoverUrl(prof.cover_image);
          if (Array.isArray(rules.days)) setWorkDays(Array.from({ length: 7 }, (_, i) => rules.days.includes(i)));
          if (rules.start) setWorkStart(rules.start);
          if (rules.end) setWorkEnd(rules.end);
          setAccepted(true);
          const svc = await axios.get(`${API}/services?provider_id=${prof.user_id}`).then(r => r.data).catch(() => []);
          if (svc.length) setServices(svc.map(s => ({
            service_id: s.service_id, title: s.title || '', description: s.description || '',
            price: s.base_price != null ? String(s.base_price) : '',
            price_min: s.price_min != null ? String(s.price_min) : '',
            price_max: s.price_max != null ? String(s.price_max) : '',
            unit: s.price_type || 'fixed',
          })));
        }
      } catch (err) {
        console.error('Profile prefill failed:', err);
      } finally {
        setInitializing(false);
      }
    })();
  }, [authLoading, token]);

  // Pre-fill the private contact fields from the signed-in account (name/email/phone/avatar),
  // without clobbering anything the provider has already typed.
  useEffect(() => {
    if (!user) return;
    const parts = (user.name || '').trim().split(/\s+/);
    setForm(f => ({
      ...f,
      first_name: f.first_name || parts[0] || '',
      last_name: f.last_name || parts.slice(1).join(' ') || '',
      email: f.email || user.email || '',
      phone: f.phone || user.phone || '',
    }));
    if (user.avatar_url) setAvatarUrl(prev => prev || user.avatar_url);
  }, [user]);

  const uploadAvatar = async (file) => {
    if (!file) return;
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/profile/avatar`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setAvatarUrl(r.data.avatar_url);
    } catch (err) { console.error('Avatar upload failed:', err); }
    finally { setAvatarBusy(false); }
  };

  const uploadPhotos = async (files) => {
    const remaining = 6 - photos.length;
    const toUpload = [...files].slice(0, remaining);
    if (!toUpload.length) return;
    setPhotosBusy(true);
    try {
      for (const file of toUpload) {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
        setPhotos(p => [...p, `/api/files/${r.data.path}`]);
      }
    } catch (err) { console.error('Photo upload failed:', err); }
    finally { setPhotosBusy(false); }
  };

  // Cover photo — the wide banner shown on the Browse card + profile header.
  const uploadCover = async (file) => {
    if (!file) return;
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setCoverUrl(`/api/files/${r.data.path}`);
    } catch (err) { console.error('Cover upload failed:', err); }
    finally { setCoverBusy(false); }
  };

  // Portfolio video — a real file the vendor drops in (max 100 MB). Stored on the
  // persistent volume; shows on their profile alongside any video link.
  const uploadVideo = async (file) => {
    if (!file) return;
    setVideoErr('');
    if (!file.type.startsWith('video/')) { setVideoErr('That doesn’t look like a video file.'); return; }
    if (file.size > 100 * 1024 * 1024) { setVideoErr('Video is too large — max 100 MB. Try trimming it or paste a link instead.'); return; }
    setVideoBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setVideoFile(`/api/files/${r.data.path}`);
    } catch (err) {
      setVideoErr(err.response?.status === 413 ? 'Video is too large — max 100 MB.' : 'Upload failed. Try again or paste a link.');
    } finally { setVideoBusy(false); }
  };

  // Documents — menus, press kits, pricing sheets (PDF/doc, up to 15 MB each, 5 max).
  const uploadDocs = async (files) => {
    setDocErr('');
    const remaining = 5 - documents.length;
    const toUpload = [...files].slice(0, remaining);
    if (!toUpload.length) return;
    setDocBusy(true);
    try {
      for (const file of toUpload) {
        if (file.size > 15 * 1024 * 1024) { setDocErr(`"${file.name}" is over 15 MB — skipped.`); continue; }
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
        setDocuments(d => [...d, { name: file.name, path: `/api/files/${r.data.path}` }]);
      }
    } catch (err) { setDocErr('Upload failed. Try again.'); }
    finally { setDocBusy(false); }
  };

  // Load the vendor's private Bookr calendar-feed link + any connected calendar.
  useEffect(() => {
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/calendar/feed-url`, h)
      .then(r => setFeedUrl(`${process.env.REACT_APP_BACKEND_URL}${r.data.path}`))
      .catch(() => {});
    axios.get(`${API}/calendar/external`, h)
      .then(r => setExtCalUrl(r.data.url || ''))
      .catch(() => {});
  }, [token]);

  const copyFeed = () => {
    navigator.clipboard?.writeText(feedUrl);
    setFeedCopied(true);
    setTimeout(() => setFeedCopied(false), 2000);
  };

  const connectCalendar = async () => {
    setCalBusy(true); setCalMsg('');
    try {
      const r = await axios.post(`${API}/calendar/external`, { url: extCalUrl.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setCalMsg(r.data.connected
        ? `Connected — ${r.data.blocked_dates} busy ${r.data.blocked_dates === 1 ? 'day' : 'days'} blocked on Bookr.`
        : 'Calendar disconnected.');
    } catch (err) {
      setCalMsg(err.response?.data?.detail || 'Could not connect that calendar. Check the link.');
    } finally { setCalBusy(false); }
  };

  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const validServices = services.filter(s =>
      s.title.trim() && (
        s.unit === 'quote' ||
        (s.unit === 'range' && Number(s.price_min) > 0 && Number(s.price_max) >= Number(s.price_min)) ||
        Number(s.price) > 0
      )
    );
    if (!form.business_name.trim()) { setError('Please add your business / display name.'); return; }
    if (!form.category) { setError('Please pick your main service category.'); return; }
    if (!validServices.length) { setError('Add at least one service — set a price, a min/max range, or mark it custom quote.'); return; }
    if (!workDays.some(Boolean)) { setError('Select at least one day you can work.'); return; }
    if (!accepted) { setError('Please accept the provider terms to continue.'); return; }

    setLoading(true);
    try {
      // Save contact info on the account — booking texts/emails go here
      try {
        await axios.put(`${API}/profile`, { phone: form.phone, email: form.email || undefined }, headers);
      } catch {
        await axios.put(`${API}/profile`, { phone: form.phone }, headers);
      }

      const first = validServices[0];
      // Mirror the first service's pricing onto the profile so display surfaces
      // (which read the profile, not services) render correctly. quote/range
      // carry no single fixed number → hourly_rate/package_price stay null.
      const hourly_rate = first.unit === 'hourly' ? Number(first.price) : null;
      const package_price = (first.unit === 'hourly' || first.unit === 'quote' || first.unit === 'range')
        ? null : Number(first.price);
      const price_type = first.unit;   // 'hourly'|'fixed'|'per_person'|'quote'|'range'
      const price_min = first.unit === 'range' ? Number(first.price_min) : null;
      const price_max = first.unit === 'range' ? Number(first.price_max) : null;
      const weekly = workDays.map((on, i) => (on ? i : null)).filter(v => v !== null);

      const profilePayload = {
        business_name: form.business_name.trim(),
        category: form.category,
        description: form.description,
        hourly_rate, package_price,
        price_type, price_min, price_max,
        city: form.city, service_radius_km: Number(form.service_radius || 25),
        cover_image: coverUrl || (photos && photos[0]) || null,
        portfolio_images: photos,
        documents,
        portfolio_videos: [
          ...(videoFile ? [videoFile] : []),
          ...(form.video_url.trim() ? [form.video_url.trim()] : []),
        ],
        special_offer: form.special_offer,
        deposit_pct: Number(form.deposit_pct),
        cancellation_policy: form.cancellation_policy,
        cancel_fee_window_days: Number(form.cancel_fee_window_days) || 0,
        cancel_fee_type: form.cancel_fee_type,
        cancel_fee_amount: Number(form.cancel_fee_amount) || 0,
        years_experience: form.experience ? Number(form.experience) : null,
        instagram: form.instagram.trim() || null,
        accepted_terms: true,
        weekly_availability: weekly,
        work_start: workStart, work_end: workEnd,
      };
      const serviceItems = validServices.map(s => ({
        service_id: s.service_id || undefined,
        title: s.title.trim(), category: form.category, description: s.description.trim(),
        price_type: s.unit,
        base_price: s.unit === 'quote' ? 0
          : s.unit === 'range' ? Number(s.price_min)
          : Number(s.price),
        price_min: s.unit === 'range' ? Number(s.price_min) : null,
        price_max: s.unit === 'range' ? Number(s.price_max) : null,
        tags: [form.category.toLowerCase()],
      }));

      if (editMode) {
        // Existing provider: update in place (POST would create a duplicate profile).
        await axios.put(`${API}/providers/profile`, profilePayload, headers);
        await axios.put(`${API}/providers/services`, { services: serviceItems }, headers);
      } else {
        await axios.post(`${API}/providers/profile`, profilePayload, headers);
        for (const item of serviceItems) {
          await axios.post(`${API}/services`, item, headers);
        }
      }

      // Refresh the in-memory role → provider, so the dashboard is reachable without re-login
      if (checkAuth) await checkAuth();
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  const connectPayouts = async () => {
    setPayoutBusy(true);
    try {
      const r = await axios.post(`${API}/providers/payouts/onboard`, { origin_url: window.location.origin }, headers);
      if (r.data.url) { window.location.href = r.data.url; return; }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Payouts aren’t available yet — you can connect your bank later from your dashboard.');
    }
    setPayoutBusy(false);
  };

  const activeStep = success ? 1 : 0;

  if (initializing) {
    return (
      <div className="min-h-screen bg-background" data-testid="provider-onboarding">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
            <span className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
            <span className="text-sm">Loading your profile…</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="provider-onboarding">
      {coverCropFile && (
        <CoverCropper
          file={coverCropFile}
          onCancel={() => setCoverCropFile(null)}
          onCropped={(cropped) => { setCoverCropFile(null); uploadCover(cropped); }}
        />
      )}
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <EmailVerifyBanner />
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-foreground mb-2">{editMode ? 'Edit your profile' : 'Set up your provider profile'}</h1>
        <p className="text-muted-foreground text-sm leading-relaxed mb-6">{editMode ? 'Update what customers see across Bookr. Changes go live as soon as you save.' : 'Everything a customer needs to find, trust, and book you. Takes about 5 minutes.'}</p>

        {/* Step indicator — signup only */}
        {!editMode && (
        <div className="flex items-center gap-2 mb-8" aria-label="Onboarding progress">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors ${i <= activeStep ? 'bg-primary text-primary-foreground shadow-soft' : 'bg-secondary text-primary'}`}>{i + 1}</span>
                <span className={`text-xs font-medium hidden sm:inline ${i <= activeStep ? 'text-foreground' : 'text-muted-foreground'}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 rounded-full ${i < activeStep ? 'bg-primary' : 'bg-secondary'}`} />}
            </React.Fragment>
          ))}
        </div>
        )}

        {/* Perks — signup only */}
        {!success && !editMode && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="provider-perks">
            {PERKS.map((p, i) => (
              <div key={i} className="bg-card border border-border/70 rounded-2xl shadow-soft p-4 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                <span className="text-lg block mb-1.5">{p.icon}</span>
                <h4 className="text-xs font-semibold text-foreground mb-0.5">{p.title}</h4>
                <p className="text-[11px] text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        )}

        {success && editMode ? (
          <div className="bg-card border border-border/70 rounded-2xl shadow-soft p-8 text-center" data-testid="edit-saved">
            <div className="w-14 h-14 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={30} weight="fill" className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-display font-semibold tracking-tight text-foreground mb-2">Changes saved</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">Your profile is updated and live. Customers will see the changes right away.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/provider/dashboard" className="inline-flex items-center justify-center bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90" data-testid="back-to-dashboard-cta">
                Back to dashboard
              </Link>
              <button type="button" onClick={() => window.location.reload()} className="inline-flex items-center justify-center border border-border bg-card text-foreground rounded-xl px-6 h-11 text-sm font-medium btn-anim hover:bg-secondary/60 cursor-pointer">
                Keep editing
              </button>
            </div>
          </div>
        ) : success ? (
          <div className="bg-card border border-border/70 rounded-2xl shadow-soft p-8 text-center" data-testid="signup-success">
            <div className="w-14 h-14 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={30} weight="fill" className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-display font-semibold tracking-tight text-foreground mb-2">Application received!</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">We'll review your profile within 24 hours. One last step gets you paid faster:</p>

            <div className="bg-secondary/50 border border-border/60 rounded-xl p-4 text-left mb-4 flex items-start gap-3">
              <Bank size={22} className="text-primary shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground mb-0.5">Connect your bank to get paid</p>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">Bookr pays out through Stripe. Link your bank account so your earnings land automatically after each event. We never see your bank details.</p>
                <button onClick={connectPayouts} disabled={payoutBusy}
                  className="bg-primary text-primary-foreground rounded-xl px-5 h-10 text-xs font-semibold btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-50 cursor-pointer" data-testid="connect-payouts-cta">
                  {payoutBusy ? 'Opening…' : 'Connect bank account'}
                </button>
              </div>
            </div>

            <Link to="/provider/dashboard" className="inline-block text-sm font-semibold text-primary hover:underline" data-testid="go-to-dashboard-cta">
              Skip for now — go to my dashboard →
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-card border border-border/70 rounded-2xl shadow-soft p-6 sm:p-8 space-y-6" data-testid="provider-signup-form">

            <Section n="1" title="Your business" subtitle="How you appear to customers across Bookr.">
              <div>
                <label className={labelCls}>Business / display name</label>
                <input type="text" value={form.business_name} onChange={e => set('business_name', e.target.value)} required placeholder="e.g. DJ Kenza, Elite Catering Co." className={inputCls} data-testid="business-name-input" />
                <p className="text-[11px] text-muted-foreground mt-1">This is the name shown on the homepage, in search, and on bookings.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Main category</label>
                  <select value={form.category} onChange={e => set('category', e.target.value)} required className={`${inputCls} cursor-pointer`} data-testid="category-select">
                    <option value="">Select</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><label className={labelCls}>Years of experience</label>
                  <input type="number" min="0" value={form.experience} onChange={e => set('experience', e.target.value)} placeholder="e.g. 5" className={inputCls} data-testid="experience-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>City you serve</label>
                  <input type="text" value={form.city} onChange={e => set('city', e.target.value)} required className={inputCls} data-testid="city-input" /></div>
                <div><label className={labelCls}>Service radius (km)</label>
                  <input type="number" min="1" value={form.service_radius} onChange={e => set('service_radius', e.target.value)} className={inputCls} data-testid="radius-input" /></div>
              </div>
            </Section>

            <Section n="2" title="Contact (private)" subtitle="Only Bookr and confirmed customers see this.">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>First name</label>
                  <input type="text" value={form.first_name} onChange={e => set('first_name', e.target.value)} required className={inputCls} data-testid="first-name-input" /></div>
                <div><label className={labelCls}>Last name</label>
                  <input type="text" value={form.last_name} onChange={e => set('last_name', e.target.value)} required className={inputCls} data-testid="last-name-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Email</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required className={inputCls} data-testid="email-input" />
                  <p className="text-[11px] text-muted-foreground mt-1">Booking confirmations go here</p></div>
                <div><label className={labelCls}>Phone</label>
                  <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} required className={inputCls} data-testid="phone-input" />
                  <p className="text-[11px] text-muted-foreground mt-1">You'll get a text when you're booked</p></div>
              </div>
            </Section>

            <Section n="3" title="Your services & pricing" subtitle="Add each package you offer. At least one is required.">
              {services.map((s, i) => (
                <div key={i} className="border border-border/70 rounded-xl p-4 space-y-3 bg-secondary/20" data-testid={`service-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">Service {i + 1}</span>
                    {services.length > 1 && (
                      <button type="button" onClick={() => removeService(i)} className="text-muted-foreground hover:text-red-500 btn-anim cursor-pointer" aria-label="Remove service" data-testid={`remove-service-${i}`}><Trash size={14} /></button>
                    )}
                  </div>
                  <input type="text" value={s.title} onChange={e => setService(i, 'title', e.target.value)} placeholder="Package name — e.g. 4-hour wedding set" className={inputCls} data-testid={`service-title-${i}`} />
                  <div className="grid grid-cols-2 gap-3">
                    {s.unit === 'quote' ? (
                      <div className="flex items-center px-3 rounded-xl border border-dashed border-border text-xs text-muted-foreground" data-testid={`service-quote-note-${i}`}>
                        No price shown — customers request a quote.
                      </div>
                    ) : s.unit === 'range' ? (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                          <CurrencyDollar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <input type="number" min="0" step="1" value={s.price_min} onChange={e => setService(i, 'price_min', e.target.value)} placeholder="Min" className={`${inputCls} pl-8`} data-testid={`service-price-min-${i}`} />
                        </div>
                        <div className="relative">
                          <CurrencyDollar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <input type="number" min="0" step="1" value={s.price_max} onChange={e => setService(i, 'price_max', e.target.value)} placeholder="Max" className={`${inputCls} pl-8`} data-testid={`service-price-max-${i}`} />
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        <CurrencyDollar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input type="number" min="0" step="1" value={s.price} onChange={e => setService(i, 'price', e.target.value)} placeholder="Price" className={`${inputCls} pl-8`} data-testid={`service-price-${i}`} />
                      </div>
                    )}
                    <select value={s.unit} onChange={e => setService(i, 'unit', e.target.value)} className={`${inputCls} cursor-pointer`} data-testid={`service-unit-${i}`}>
                      {UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                    </select>
                  </div>
                  <textarea rows={2} value={s.description} onChange={e => setService(i, 'description', e.target.value)} placeholder="What's included? (optional)" className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none" data-testid={`service-desc-${i}`} />
                </div>
              ))}
              {services.length < 6 && (
                <button type="button" onClick={addService} className="w-full border border-dashed border-border rounded-xl h-11 text-xs font-semibold text-primary hover:bg-secondary/50 btn-anim cursor-pointer flex items-center justify-center gap-1.5" data-testid="add-service-btn">
                  <Plus size={14} weight="bold" /> Add another service
                </button>
              )}
            </Section>

            <Section n="4" title="About your work" subtitle="Sell yourself — this shows on your profile.">
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Tell clients what makes you great — your style, experience, what people love about working with you..." className="w-full rounded-xl border border-border bg-card px-3 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none" data-testid="description-input" />
              <div><label className={labelCls}>Special offer (optional)</label>
                <input type="text" maxLength={80} value={form.special_offer} onChange={e => set('special_offer', e.target.value)} placeholder="e.g. 10% off Sunday events" className={inputCls} data-testid="special-offer-input" />
                <p className="text-[11px] text-muted-foreground mt-1">Shown as a banner on your profile and a chip in search</p></div>
            </Section>

            <Section n="5" title="Availability" subtitle="Which days do you typically work? Customers can only book open days.">
              <div className="flex flex-wrap gap-2" data-testid="weekly-availability">
                {DAYS.map((d, i) => (
                  <button key={d} type="button" onClick={() => toggleDay(i)}
                    className={`w-11 h-11 rounded-xl text-xs font-semibold btn-anim cursor-pointer ${workDays[i] ? 'bg-primary text-primary-foreground shadow-soft' : 'bg-secondary text-muted-foreground hover:bg-secondary/70'}`}
                    aria-pressed={workDays[i]} data-testid={`day-${i}`}>{d[0]}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CalendarBlank size={15} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Typical hours</span>
                <select value={workStart} onChange={e => setWorkStart(e.target.value)} className="h-9 rounded-lg border border-border bg-card px-2 text-xs cursor-pointer" data-testid="work-start">
                  {TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <span className="text-xs text-muted-foreground">to</span>
                <select value={workEnd} onChange={e => setWorkEnd(e.target.value)} className="h-9 rounded-lg border border-border bg-card px-2 text-xs cursor-pointer" data-testid="work-end">
                  {TIMES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <p className="text-[11px] text-muted-foreground">You can fine-tune individual dates on your dashboard calendar anytime.</p>

              {/* ── Calendar sync ── */}
              <div className="rounded-xl border border-border bg-secondary/30 p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <CalendarBlank size={16} weight="fill" className="text-primary" />
                  <span className="text-sm font-semibold text-foreground">Sync with your calendar <span className="font-normal text-muted-foreground">(optional)</span></span>
                </div>

                {/* IN: block busy dates from their calendar */}
                <div>
                  <label className={labelCls}>Connect your calendar</label>
                  <p className="text-[11px] text-muted-foreground mb-2">Paste your calendar's secret <span className="font-medium">iCal / .ics link</span> (Google, Apple, Outlook all have one). We'll block those busy days so you're never double-booked.</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input type="url" value={extCalUrl} onChange={e => setExtCalUrl(e.target.value)} placeholder="https://calendar.google.com/…/basic.ics" className={inputCls} data-testid="ext-cal-input" />
                    <button type="button" onClick={connectCalendar} disabled={calBusy}
                      className="shrink-0 bg-primary text-primary-foreground text-sm font-semibold px-4 h-11 rounded-xl btn-anim hover:opacity-95 cursor-pointer disabled:opacity-60" data-testid="ext-cal-connect">
                      {calBusy ? 'Connecting…' : extCalUrl.trim() ? 'Connect' : 'Clear'}
                    </button>
                  </div>
                  {calMsg && <p className="text-xs text-primary mt-1.5">{calMsg}</p>}

                  {/* Where-to-find-it help — the #1 place vendors get stuck.
                      Native <details> keeps it collapsed by default, no JS/state. */}
                  <details className="mt-2 group" data-testid="cal-help">
                    <summary className="text-[11px] font-semibold text-primary cursor-pointer select-none list-none flex items-center gap-1 hover:underline">
                      <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                      Where do I find my calendar link?
                    </summary>
                    <div className="mt-2 space-y-3 rounded-lg bg-card border border-border/60 p-3">
                      {CAL_HELP.map(c => (
                        <div key={c.name}>
                          <p className="text-[12px] font-semibold text-foreground mb-0.5">{c.emoji} {c.name}</p>
                          <ol className="text-[11px] text-muted-foreground leading-relaxed list-decimal pl-4 space-y-0.5">
                            {c.steps.map((s, i) => <li key={i}>{s}</li>)}
                          </ol>
                        </div>
                      ))}
                      <p className="text-[10px] text-muted-foreground/80 border-t border-border/50 pt-2">
                        Tip: it's the <span className="font-medium">secret / private</span> link (not the "share with people" one), and a link starting with <span className="font-mono">webcal://</span> works too — we handle it.
                      </p>
                    </div>
                  </details>
                </div>

                {/* OUT: their Bookr bookings feed */}
                {feedUrl && (
                  <div>
                    <label className={labelCls}>Add Bookr to your calendar</label>
                    <p className="text-[11px] text-muted-foreground mb-2">Subscribe to this link in your calendar app and your Bookr bookings show up automatically.</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input readOnly value={feedUrl} onFocus={e => e.target.select()} className={`${inputCls} text-muted-foreground`} data-testid="bookr-feed-url" />
                      <button type="button" onClick={copyFeed}
                        className="shrink-0 border border-border bg-card text-foreground text-sm font-semibold px-4 h-11 rounded-xl btn-anim hover:bg-secondary/60 cursor-pointer" data-testid="bookr-feed-copy">
                        {feedCopied ? 'Copied ✓' : 'Copy link'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Section>

            <Section n="6" title="Photos & media" subtitle="Profiles with photos get booked far more often.">
              {/* Cover photo — the big banner customers see on the Browse card + your profile header */}
              <div>
                <label className={labelCls}>Cover photo <span className="font-normal text-muted-foreground">— shown on Browse &amp; your profile header</span></label>
                {coverUrl ? (
                  <div className="relative group">
                    <img src={backendUrl(coverUrl)} alt="Cover" className="w-full aspect-[2.1] object-cover rounded-xl border border-border shadow-soft" data-testid="cover-preview" />
                    <label className="absolute bottom-2 right-2 bg-card/95 border border-border text-foreground text-xs font-semibold px-3 py-1.5 rounded-lg btn-anim hover:bg-secondary cursor-pointer shadow-soft">
                      {coverBusy ? 'Uploading…' : 'Change cover'}
                      <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => { if (e.target.files[0]) setCoverCropFile(e.target.files[0]); e.target.value = ''; }} data-testid="cover-upload-input" />
                    </label>
                  </div>
                ) : (
                  <label className="w-full h-40 sm:h-48 rounded-xl border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/60 hover:border-primary/40 transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer text-muted-foreground hover:text-primary" data-testid="cover-dropzone">
                    <Camera size={26} />
                    <span className="text-xs font-semibold">{coverBusy ? 'Uploading…' : 'Upload your cover photo'}</span>
                    <span className="text-[10px]">Wide shot of your best work — this is your first impression</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => uploadCover(e.target.files[0])} data-testid="cover-upload-input" />
                  </label>
                )}
              </div>
              <div>
                <label className={labelCls}>Profile picture / logo <span className="font-normal text-muted-foreground">— your round avatar</span></label>
                <div className="flex items-center gap-4">
                  {avatarUrl ? (
                    <img src={backendUrl(avatarUrl)} alt="Your profile" className="w-16 h-16 rounded-full object-cover border border-border shadow-soft" />
                  ) : (
                    <span className="w-16 h-16 rounded-full bg-secondary text-primary flex items-center justify-center"><Camera size={22} /></span>
                  )}
                  <label className="border border-border bg-card text-foreground text-xs font-semibold px-4 py-2.5 rounded-xl btn-anim hover:bg-secondary/60 cursor-pointer">
                    {avatarBusy ? 'Uploading…' : avatarUrl ? 'Change photo' : 'Upload photo'}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => uploadAvatar(e.target.files[0])} data-testid="avatar-upload-input" />
                  </label>
                </div>
              </div>
              <div>
                <label className={labelCls}>Portfolio photos <span className="font-normal text-muted-foreground">(up to 6)</span></label>
                <p className="text-[11px] text-muted-foreground mb-2">More shots of your work — these appear in your profile gallery.</p>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {photos.map((src, i) => (
                    <div key={i} className="relative group aspect-square">
                      <img src={backendUrl(src)} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover rounded-xl border border-border" />
                      <button type="button" aria-label="Remove photo" onClick={() => setPhotos(p => p.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-foreground text-background rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"><X size={10} weight="bold" /></button>
                    </div>
                  ))}
                  {photos.length < 6 && (
                    <label className="aspect-square rounded-xl border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/60 hover:border-primary/40 transition-colors flex flex-col items-center justify-center gap-1 cursor-pointer text-muted-foreground hover:text-primary">
                      <Images size={18} />
                      <span className="text-[9px] font-medium">{photosBusy ? 'Uploading…' : 'Add'}</span>
                      <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={e => uploadPhotos(e.target.files)} data-testid="portfolio-upload-input" />
                    </label>
                  )}
                </div>
              </div>
              {/* Portfolio video — drop in a real file (or paste a link below) */}
              <div>
                <label className={labelCls}>Showcase video <span className="font-normal text-muted-foreground">(optional) — drop in a clip of your work</span></label>
                {videoFile ? (
                  <div className="relative">
                    <video src={backendUrl(videoFile)} controls className="w-full max-h-64 rounded-xl border border-border bg-black" data-testid="video-preview" />
                    <button type="button" onClick={() => setVideoFile(null)} className="absolute top-2 right-2 bg-foreground/90 text-background text-xs font-semibold px-3 py-1.5 rounded-lg btn-anim cursor-pointer">Remove</button>
                  </div>
                ) : (
                  <label className="w-full rounded-xl border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/60 hover:border-primary/40 transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer text-muted-foreground hover:text-primary py-8" data-testid="video-dropzone">
                    <Images size={26} />
                    <span className="text-xs font-semibold">{videoBusy ? 'Uploading…' : 'Drop a video file or click to browse'}</span>
                    <span className="text-[10px]">MP4, MOV or WebM · up to 100 MB</span>
                    <input type="file" accept="video/*" className="hidden" onChange={e => uploadVideo(e.target.files[0])} data-testid="video-upload-input" />
                  </label>
                )}
                {videoErr && <p className="text-xs text-destructive mt-1.5">{videoErr}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={labelCls}>Or paste a video link (optional)</label>
                  <input type="url" value={form.video_url} onChange={e => set('video_url', e.target.value)} placeholder="YouTube or Vimeo link" className={inputCls} data-testid="video-input" /></div>
                <div><label className={labelCls}>Instagram (optional)</label>
                  <input type="text" value={form.instagram} onChange={e => set('instagram', e.target.value)} placeholder="@yourhandle" className={inputCls} data-testid="instagram-input" /></div>
              </div>
              {/* Documents — menu, press kit, pricing sheet, portfolio deck */}
              <div>
                <label className={labelCls}>Documents <span className="font-normal text-muted-foreground">(optional) — menu, price sheet, press kit</span></label>
                {documents.length > 0 && (
                  <div className="space-y-2 mb-2">
                    {documents.map((doc, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5">
                        <FileText size={20} weight="fill" className="text-primary shrink-0" />
                        <span className="text-sm text-foreground truncate flex-1" title={doc.name}>{doc.name}</span>
                        <button type="button" aria-label="Remove document" onClick={() => setDocuments(d => d.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive cursor-pointer shrink-0"><X size={15} weight="bold" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {documents.length < 5 && (
                  <label className="w-full rounded-xl border-2 border-dashed border-border bg-secondary/30 hover:bg-secondary/60 hover:border-primary/40 transition-colors flex flex-col items-center justify-center gap-2 cursor-pointer text-muted-foreground hover:text-primary py-6" data-testid="doc-dropzone">
                    <FileArrowUp size={24} />
                    <span className="text-xs font-semibold">{docBusy ? 'Uploading…' : 'Drop a file or click to browse'}</span>
                    <span className="text-[10px]">PDF, Word, images · up to 15 MB each · 5 max</span>
                    <input type="file" accept=".pdf,.doc,.docx,image/*" multiple className="hidden" onChange={e => uploadDocs(e.target.files)} data-testid="doc-upload-input" />
                  </label>
                )}
                {docErr && <p className="text-xs text-destructive mt-1.5">{docErr}</p>}
              </div>
            </Section>

            <Section n="7" title="Booking & payment terms" subtitle="How you take payment and handle cancellations.">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={labelCls}>Deposit to book</label>
                  <select value={form.deposit_pct} onChange={e => set('deposit_pct', e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="deposit-select">
                    <option value="0">Full payment upfront</option>
                    <option value="25">25% deposit, balance later</option>
                    <option value="50">50% deposit, balance later</option>
                  </select>
                  <p className="text-[11px] text-muted-foreground mt-1">Balance auto-collected 7 days before the event</p></div>
                <div><label className={labelCls}>Cancellation policy</label>
                  <select value={form.cancellation_policy} onChange={e => set('cancellation_policy', e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="policy-select">
                    <option value="flexible">Flexible — full refund up to 48h before</option>
                    <option value="moderate">Moderate — full refund up to 7 days before</option>
                    <option value="strict">Strict — deposit non-refundable</option>
                    <option value="custom">Custom — set your own fee & window</option>
                  </select></div>
              </div>
              {form.cancellation_policy === 'custom' && (
                <div className="bg-secondary/40 rounded-xl p-3.5 space-y-3" data-testid="custom-cancel-fields">
                  <p className="text-[11px] text-muted-foreground leading-relaxed">Customers who cancel <span className="font-medium text-foreground">within your window</span> before the event are charged this fee; cancelling earlier is free (full refund).</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><label className={labelCls}>Fee applies within</label>
                      <div className="relative">
                        <input type="number" min="0" max="365" value={form.cancel_fee_window_days} onChange={e => set('cancel_fee_window_days', e.target.value)} className={`${inputCls} pr-14`} data-testid="cancel-window-input" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">days</span>
                      </div></div>
                    <div><label className={labelCls}>Fee type</label>
                      <select value={form.cancel_fee_type} onChange={e => set('cancel_fee_type', e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="cancel-fee-type">
                        <option value="percent">% of total</option>
                        <option value="flat">Flat $ amount</option>
                      </select></div>
                    <div><label className={labelCls}>Fee amount</label>
                      <div className="relative">
                        {form.cancel_fee_type === 'flat' && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">$</span>}
                        <input type="number" min="0" max={form.cancel_fee_type === 'percent' ? '100' : undefined} value={form.cancel_fee_amount} onChange={e => set('cancel_fee_amount', e.target.value)} className={`${inputCls} ${form.cancel_fee_type === 'flat' ? 'pl-7' : 'pr-8'}`} data-testid="cancel-fee-amount" />
                        {form.cancel_fee_type === 'percent' && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">%</span>}
                      </div></div>
                  </div>
                  <p className="text-[11px] text-primary/90 bg-card rounded-lg px-3 py-2 border border-primary/15">
                    {Number(form.cancel_fee_window_days) > 0
                      ? `Cancel within ${form.cancel_fee_window_days} day${Number(form.cancel_fee_window_days) === 1 ? '' : 's'} of the event → ${form.cancel_fee_type === 'flat' ? `$${form.cancel_fee_amount || 0}` : `${form.cancel_fee_amount || 0}%`} fee. Earlier → free.`
                      : 'Any cancellation is free (0-day window).'}
                  </p>
                </div>
              )}
              {!editMode && (
                <div className="flex items-start gap-2.5 bg-secondary/40 rounded-xl p-3">
                  <Bank size={18} className="text-primary shrink-0 mt-0.5" />
                  <p className="text-[11px] text-muted-foreground leading-relaxed">To receive money you'll connect your bank through Stripe right after you submit — no card or bank numbers are entered here.</p>
                </div>
              )}
            </Section>

            {/* Terms + submit */}
            <div className="border-t border-border/60 pt-6">
              {!editMode && (
                <label className="flex items-start gap-2.5 cursor-pointer mb-4">
                  <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 accent-primary cursor-pointer" data-testid="accept-terms" />
                  <span className="text-xs text-muted-foreground leading-relaxed">I agree to the <Link to="/legal/terms" className="text-primary hover:underline" target="_blank">Provider Terms</Link> and the 6% Bookr service fee, and confirm my listings are accurate.</span>
                </label>
              )}
              {error && <p className="text-sm text-red-500 mb-3" data-testid="onboarding-error">{error}</p>}
              <button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground rounded-xl px-6 h-12 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 disabled:opacity-50 cursor-pointer" data-testid="submit-profile-button">
                {loading ? (editMode ? 'Saving…' : 'Submitting…') : (editMode ? 'Save changes' : 'Submit application')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
